import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Database,
  Upload,
  Mail,
  HardDrive,
  Code,
  DatabaseZap,
  ChevronDown,
  Ear,
  Facebook,
  Instagram,
  Star,
  Smartphone,
  Store,
  Plug,
  Headset,
  LifeBuoy,
  Briefcase,
  BookOpen,
  ShoppingBag,
  Menu,
  X,
  Megaphone,
  Zap,
  BellRing,
  MousePointerClick,
  LayoutGrid,
  RefreshCw,
  Globe,
  Webhook,
  MonitorSmartphone,
  ArrowLeft,
} from "lucide-react";
import { useAdminConnectionCounts } from "@/hooks/useAdminConnectionCounts";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ManualUploads from "@/components/admin/ManualUploads";
import GoogleDriveConnector from "@/components/admin/GoogleDriveConnector";
import GmailConnector from "@/components/admin/GmailConnector";
import ScriptsManager from "@/components/admin/ScriptsManager";
import DatabaseConnections from "@/components/admin/DatabaseConnections";
import GenericConnectorPage from "@/components/admin/GenericConnectorPage";
import AllConnectors from "@/components/admin/AllConnectors";
import SyncingMonitor from "@/pages/SyncingMonitor";

const dataSourceItems = [
  { id: "ds-sql-db", label: "Databases", icon: DatabaseZap, dbType: null },
  { id: "ds-scripts", label: "Scripts", icon: Code, dbType: null },
  { id: "ds-manual-uploads", label: "Manual Uploads", icon: Upload, dbType: null },
  { id: "ds-gmail", label: "Gmail", icon: Mail, dbType: null },
  { id: "ds-google-drive", label: "Google Drive", icon: HardDrive, dbType: null },
  { id: "ds-rest-apis", label: "Rest APIs", icon: Globe, dbType: "rest_api" },
  { id: "ds-webhooks", label: "Web hooks", icon: Webhook, dbType: "webhook" },
  { id: "ds-frontend-sdks", label: "Frontend SDKs", icon: MonitorSmartphone, dbType: "frontend_sdk" },
];

const socialListeningItems = [
  { id: "sl-facebook", label: "Facebook", icon: Facebook, dbType: "facebook" },
  { id: "sl-instagram", label: "Instagram", icon: Instagram, dbType: "instagram" },
  { id: "sl-google-reviews", label: "Google Reviews", icon: Star, dbType: "google_reviews" },
  { id: "sl-playstore", label: "Playstore", icon: Smartphone, dbType: "playstore" },
  { id: "sl-appstore", label: "Appstore", icon: Store, dbType: "appstore" },
];

const otherConnectorItems = [
  { id: "oc-freshdesk", label: "Freshdesk", icon: Headset, dbType: "freshdesk" },
  { id: "oc-zendesk", label: "Zendesk", icon: LifeBuoy, dbType: "zendesk" },
  { id: "oc-salesforce", label: "Salesforce", icon: Briefcase, dbType: "salesforce" },
  { id: "oc-zoho", label: "Zoho", icon: BookOpen, dbType: "zoho" },
  { id: "oc-shopify", label: "Shopify", icon: ShoppingBag, dbType: "shopify" },
];

const marketingItems = [
  { id: "mk-webengage", label: "WebEngage", icon: MousePointerClick, dbType: "webengage" },
  { id: "mk-moengage", label: "MoEngage", icon: BellRing, dbType: "moengage" },
  { id: "mk-clevertap", label: "CleverTap", icon: Zap, dbType: "clevertap" },
];

const allItems = [...dataSourceItems, ...socialListeningItems, ...otherConnectorItems, ...marketingItems];

const DataConnectors = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const counts = useAdminConnectionCounts();
  const [active, setActive] = useState(() => {
    const fromUrl = searchParams.get("connector");
    if (fromUrl === "all-connectors" || fromUrl === "syncing-monitor") return fromUrl;
    const valid = allItems.find((i) => i.id === fromUrl);
    return valid ? fromUrl! : "all-connectors";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const selectConnector = (id: string) => {
    setActive(id);
    setSidebarOpen(false);
    setSearchParams((prev) => {
      prev.set("connector", id);
      return prev;
    });
  };

  const isDataSourceActive = dataSourceItems.some((i) => i.id === active);
  const isSocialListeningActive = socialListeningItems.some((i) => i.id === active);
  const isOtherConnectorActive = otherConnectorItems.some((i) => i.id === active);
  const isMarketingActive = marketingItems.some((i) => i.id === active);

  const [dsOpen, setDsOpen] = useState(true);
  const [slOpen, setSlOpen] = useState(true);
  const [ocOpen, setOcOpen] = useState(true);
  const [mkOpen, setMkOpen] = useState(true);
  const activeItem = allItems.find((i) => i.id === active);

  return (
    <div className="flex flex-1 min-h-0 relative overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
          "md:relative md:inset-auto md:z-auto md:translate-x-0 md:flex md:shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <aside className="flex h-full w-56 flex-col border-r border-border bg-sidebar">
          <div className="flex items-center justify-between px-4 py-3 shrink-0">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data Connectors</h2>
            <Button variant="ghost" size="icon" className="h-6 w-6 md:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="shrink-0 border-b border-border bg-sidebar px-2 py-2 space-y-1">
            <button
              onClick={() => selectConnector("all-connectors")}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                active === "all-connectors"
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50",
              )}
            >
              <LayoutGrid className="h-4 w-4" />
              Show All Connectors
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 pb-4 overflow-y-auto">
            <Collapsible open={dsOpen} onOpenChange={setDsOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isDataSourceActive
                      ? "text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  )}
                >
                  <Database className="h-4 w-4" />
                  Data Sources
                  <ChevronDown className={cn("ml-auto h-3 w-3 transition-transform", dsOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                  {dataSourceItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => selectConnector(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active === item.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {(counts[item.id] ?? 0) > 0 && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          {counts[item.id]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={slOpen} onOpenChange={setSlOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isSocialListeningActive
                      ? "text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  )}
                >
                  <Ear className="h-4 w-4" />
                  Social Listening
                  <ChevronDown className={cn("ml-auto h-3 w-3 transition-transform", slOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                  {socialListeningItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => selectConnector(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active === item.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={ocOpen} onOpenChange={setOcOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isOtherConnectorActive
                      ? "text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  )}
                >
                  <Plug className="h-4 w-4" />
                  Other Connectors
                  <ChevronDown className={cn("ml-auto h-3 w-3 transition-transform", ocOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                  {otherConnectorItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => selectConnector(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active === item.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            <Collapsible open={mkOpen} onOpenChange={setMkOpen}>
              <CollapsibleTrigger asChild>
                <button
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isMarketingActive
                      ? "text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                  )}
                >
                  <Megaphone className="h-4 w-4" />
                  Marketing
                  <ChevronDown className={cn("ml-auto h-3 w-3 transition-transform", mkOpen && "rotate-180")} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                  {marketingItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => selectConnector(item.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                        active === item.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </nav>
        </aside>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile toolbar */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-sm font-medium">
            {active === "syncing-monitor"
              ? "Syncing Monitor"
              : active === "all-connectors"
                ? "All Connectors"
                : activeItem?.label}
          </span>
        </div>

        {active === "syncing-monitor" ? (
          <SyncingMonitor />
        ) : active === "all-connectors" ? (
          <AllConnectors onSelectConnector={selectConnector} />
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-border bg-card/50 px-4 py-2.5 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => selectConnector("all-connectors")} className="gap-1.5 text-xs font-mono">
                <ArrowLeft className="h-3.5 w-3.5" />
                All Connectors
              </Button>
              <span className="text-muted-foreground text-xs">/</span>
              <span className="text-sm font-medium">{activeItem?.label}</span>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {active === "ds-manual-uploads" ? (
                <ManualUploads />
              ) : active === "ds-gmail" ? (
                <GmailConnector />
              ) : active === "ds-google-drive" ? (
                <GoogleDriveConnector />
              ) : active === "ds-scripts" ? (
                <ScriptsManager />
              ) : active === "ds-sql-db" ? (
                <DatabaseConnections />
              ) : activeItem?.dbType ? (
                <GenericConnectorPage key={activeItem.id} label={activeItem.label} dbType={activeItem.dbType} />
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataConnectors;
